import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Input } from '../ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from '../ui/dropdown-menu';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../ui/select';
import {
  Plus, TrendingUp, TrendingDown, Scale, Layers, Trash2, X, CheckCircle2,
  FileEdit, ChevronRight, AlertTriangle, Package, Wrench, Minus, ArrowRight,
} from 'lucide-react';
import { api, salApi, extraApi, contractVariationApi } from '../../lib/api';
import { formatCurrency, isZeroMoney, moneySign } from '../../lib/utils';
import { toast } from 'sonner';
import { ExtraVariazioniPanel } from './ExtraVariazioniPanel';
import { CustomerFinishesPanel } from './CustomerFinishesPanel';
import { isExtraContractEffective } from '../../lib/extraEligibility';
import SubcontractImpactSection from './SubcontractImpactSection';

const num = (v) => (typeof v === 'number' ? v : parseFloat(v) || 0);

const CAUSALI = [
  { value: 'rinuncia_cliente', label: 'Rinuncia del cliente' },
  { value: 'impossibilita_tecnica', label: 'Impossibilità tecnica' },
  { value: 'errore_progettuale', label: 'Errore di progetto' },
  { value: 'altro', label: 'Altro' },
];

const VAR_STATUS = {
  bozza: { label: 'Bozza', cls: 'bg-amber-100 text-amber-700' },
  effective: { label: 'Effettiva', cls: 'bg-emerald-100 text-emerald-700' },
  da_gestire: { label: 'Da gestire', cls: 'bg-red-100 text-red-700' },
  annullata: { label: 'Annullata', cls: 'bg-slate-100 text-slate-500' },
};

const fmtDate = (d) => { try { return d ? new Date(d).toLocaleDateString('it-IT') : '—'; } catch { return '—'; } };

// Effetto economico leggibile (nessun termine interno S2A/S2B)
const economicEffectLine = (v) => {
  const t = v?.totals || {};
  const action = v?.economic_action;
  if (v?.status === 'da_gestire' || action === 'manual_review') return { text: 'Da gestire manualmente', tone: 'red' };
  if (action === 'separate_payment') return { text: `Pagamento separato: ${formatCurrency(num(t.excess_amount))}`, tone: 'emerald' };
  if (action === 'reduce_sal_plan') return { text: `Riduzione piano SAL: ${formatCurrency(num(t.reduction_excess_amount))}`, tone: 'red' };
  if (action === 'scope_only') return { text: 'Nessuna variazione economica', tone: 'slate' };
  // bozza / previsione
  const net = num(t.net_total);
  if (net > 0) return { text: `Pagamento separato: ${formatCurrency(num(t.excess_amount))}`, tone: 'emerald' };
  if (net < 0) return { text: `Riduzione piano SAL (se assorbibile): ${formatCurrency(num(t.reduction_excess_amount))}`, tone: 'red' };
  return { text: 'Nessuna variazione economica', tone: 'slate' };
};

export const ContractVariationsTab = ({
  projectId, extras = [], salItems = [], canManageVariations = false,
  canReadVariations = false, canEditStandalone = false, canViewEconomic = true, projectClosed = false,
  canOperateFinishes = false, onNewSelection,
  onNewExtra, onOpenExtra, onChanged,
}) => {
  const [summary, setSummary] = useState(null);
  const [variations, setVariations] = useState([]);
  const [extrasLocal, setExtrasLocal] = useState(extras || []);
  const [negatives, setNegatives] = useState([]);
  const [busy, setBusy] = useState(false);

  // Dialogs
  const [newVarOpen, setNewVarOpen] = useState(false);
  const [newVarDesc, setNewVarDesc] = useState('');
  const [composerVar, setComposerVar] = useState(null); // read model
  const [picker, setPicker] = useState(null); // 'extra' | 'negative' | null
  const [detailVar, setDetailVar] = useState(null); // effective/da_gestire read model
  const [confirmFinalize, setConfirmFinalize] = useState(false);

  // Standalone diminuzione (create bozza)
  const [negOpen, setNegOpen] = useState(false);
  const [selItemId, setSelItemId] = useState('');
  const [causale, setCausale] = useState('rinuncia_cliente');
  const [reason, setReason] = useState('');

  const loadSummary = useCallback(async () => {
    try { const r = await salApi.getContractSummary(projectId); setSummary(r.data); } catch { setSummary(null); }
  }, [projectId]);
  const loadVariations = useCallback(async () => {
    if (!canReadVariations) { setVariations([]); return; }
    try { const r = await contractVariationApi.list(projectId); setVariations(r.data || []); } catch { setVariations([]); }
  }, [projectId, canReadVariations]);
  const loadExtras = useCallback(async () => {
    try { const r = await extraApi.getByProject(projectId); setExtrasLocal(r.data || []); } catch { setExtrasLocal([]); }
  }, [projectId]);
  const loadNegatives = useCallback(async () => {
    try { const r = await api.get(`/projects/${projectId}/negative-variations`); setNegatives(r.data || []); } catch { setNegatives([]); }
  }, [projectId]);

  const reloadAll = useCallback(async () => {
    await Promise.all([loadSummary(), loadVariations(), loadExtras(), loadNegatives()]);
    onChanged && onChanged();
  }, [loadSummary, loadVariations, loadExtras, loadNegatives, onChanged]);

  useEffect(() => { loadSummary(); loadVariations(); loadExtras(); loadNegatives(); /* eslint-disable-next-line */ }, [projectId, canReadVariations]);

  // Componenti assorbiti da varianti EFFECTIVE → esclusi da "Altri movimenti"
  const effectiveVars = variations.filter((v) => v.status === 'effective');
  const absorbedExtraIds = useMemo(() => new Set(effectiveVars.flatMap((v) => (v.positive_components || []).map((c) => c.extra_id))), [variations]);
  const absorbedNegativeIds = useMemo(() => new Set(effectiveVars.flatMap((v) => (v.negative_components || []).map((c) => c.negative_variation_id))), [variations]);

  // STEP 4E.2 · Scelte cliente/Finiture: Extra fornitura PRE-EFFETTIVE con appuntamento scelta.
  // Vanno mostrate SOLO nel pannello Finiture e NASCOSTE da "Extra e diminuzioni fuori variante"
  // (no-duplicate). Una volta contrattualmente efficaci escono dal pannello e rientrano nella lista.
  const finishesExtraIds = useMemo(() => new Set(
    (extrasLocal || []).filter((e) => e.extra_type === 'fornitura' && e.choice_appointment_id && !isExtraContractEffective(e)).map((e) => e.id)
  ), [extrasLocal]);
  const hiddenStandaloneExtraIds = useMemo(() => new Set([...absorbedExtraIds, ...finishesExtraIds]), [absorbedExtraIds, finishesExtraIds]);

  // Eligibilità componenti per il composer
  const eligibleExtras = useMemo(() => (extrasLocal || []).filter(
    (e) => (e.extra_type || 'lavoro') !== 'fornitura' && !e.variation_id
  ), [extrasLocal]);
  const eligibleNegatives = useMemo(() => (negatives || []).filter(
    (n) => !n.variation_id && n.status === 'bozza' && !n.stralcio_applied
  ), [negatives]);

  const reducibleItems = (salItems || []).filter(
    (i) => (i.source || 'contratto') === 'contratto' && (i.source_type || 'lavoro') === 'lavoro' && i.item_status !== 'non_eseguita'
  );
  const selItem = reducibleItems.find((i) => i.id === selItemId);
  const selResidual = selItem ? Math.max(0, num(selItem.total_quantity) - num(selItem.completed_quantity)) * num(selItem.unit_price) : 0;

  // ---- Header economico ----
  const initial = num(summary?.initial_contract_value);
  const computed = num(summary?.current_contract_value_computed);
  const variationNet = num(summary?.variation_net_total);
  const stdPos = num(summary?.positive_variations);
  const stdNeg = num(summary?.standalone_stralci_total) + num(summary?.negative_variations_other_than_stralci);
  const totalEffect = computed - initial;
  const aumentiTotal = stdPos + num(summary?.variation_positive_total);
  const diminuzioniTotal = stdNeg + num(summary?.variation_negative_total);

  // ---- Actions ----
  const createVariation = async () => {
    setBusy(true);
    try {
      const r = await contractVariationApi.create(projectId, { description: (newVarDesc || '').trim() });
      setNewVarOpen(false); setNewVarDesc('');
      setComposerVar(r.data);
      await reloadAll();
    } catch (e) { toast.error(errMsg(e, 'Errore creazione variante')); }
    finally { setBusy(false); }
  };

  const refreshComposer = async (vid) => {
    try { const r = await contractVariationApi.getOne(vid); setComposerVar(r.data); } catch { /* noop */ }
  };

  const attach = async (kind, componentId) => {
    if (!composerVar) return;
    setBusy(true);
    try {
      const r = await contractVariationApi.attach(composerVar.id, kind, componentId);
      setComposerVar(r.data); setPicker(null);
      await Promise.all([loadExtras(), loadNegatives()]);
    } catch (e) { toast.error(errMsg(e, 'Errore associazione componente')); }
    finally { setBusy(false); }
  };

  const detach = async (kind, componentId) => {
    if (!composerVar) return;
    setBusy(true);
    try {
      const r = await contractVariationApi.detach(composerVar.id, kind, componentId);
      setComposerVar(r.data);
      await Promise.all([loadExtras(), loadNegatives()]);
    } catch (e) { toast.error(errMsg(e, 'Errore rimozione componente')); }
    finally { setBusy(false); }
  };

  const deleteBozzaVariation = async () => {
    if (!composerVar) return;
    setBusy(true);
    try {
      await contractVariationApi.cancel(composerVar.id);
      toast.success('Variante bozza eliminata');
      setComposerVar(null);
      await reloadAll();
    } catch (e) { toast.error(errMsg(e, 'Errore eliminazione')); }
    finally { setBusy(false); }
  };

  const finalizeVariation = async () => {
    if (!composerVar) return;
    setBusy(true);
    try {
      const r = await contractVariationApi.makeEffective(composerVar.id);
      const out = r.data || {};
      if (out.status === 'da_gestire' || out.outcome === 'manual_review') {
        toast.warning('Variante da gestire: la riduzione non è assorbibile automaticamente');
      } else {
        toast.success('Variante resa effettiva');
      }
      setConfirmFinalize(false); setComposerVar(null);
      await reloadAll();
    } catch (e) { toast.error(errMsg(e, 'Errore finalizzazione variante')); }
    finally { setBusy(false); }
  };

  const saveNegBozza = async () => {
    if (!selItemId) { toast.error('Seleziona la voce da ridurre'); return; }
    if ((reason || '').trim().length < 8) { toast.error('Motivazione obbligatoria (min 8 caratteri)'); return; }
    setBusy(true);
    try {
      await api.post(`/projects/${projectId}/negative-variations`, { sal_item_id: selItemId, reason: reason.trim(), category: causale });
      toast.success('Bozza di diminuzione salvata');
      setNegOpen(false); setSelItemId(''); setReason(''); setCausale('rinuncia_cliente');
      await reloadAll();
    } catch (e) { toast.error(errMsg(e, 'Errore salvataggio bozza')); }
    finally { setBusy(false); }
  };

  const openVariation = (v) => {
    if (v.status === 'bozza' && canManageVariations) { setComposerVar(v); return; }
    setDetailVar(v);
  };

  const daGestireCount = variations.filter((v) => v.status === 'da_gestire').length;

  const canFinalize = composerVar && (composerVar.negative_components || []).length >= 1;

  return (
    <div className="flex flex-col gap-6" data-testid="contract-variations-tab">
      {/* ============ RIEPILOGO ECONOMICO (in testa) ============ */}
      {canViewEconomic && summary && (
        <Card className="card-abitalo overflow-hidden order-1" data-testid="cv-economic-header">
          <CardContent className="p-0">
            <div className="grid grid-cols-2 lg:grid-cols-4 divide-x divide-y lg:divide-y-0 divide-slate-100">
              <div className="p-3.5" data-testid="cv-header-originario">
                <p className="text-[11px] text-[#737373] uppercase tracking-wide">Contratto originario</p>
                <p className="text-xl font-semibold text-[#171717] mt-0.5">{formatCurrency(initial)}</p>
              </div>
              <div className="p-3.5" data-testid="cv-header-aumenti">
                <p className="text-[11px] text-[#737373] uppercase tracking-wide">Aumenti</p>
                <p className="text-xl font-semibold text-emerald-700 mt-0.5">{isZeroMoney(aumentiTotal) ? '' : '+'}{formatCurrency(aumentiTotal)}</p>
              </div>
              <div className="p-3.5" data-testid="cv-header-diminuzioni">
                <p className="text-[11px] text-[#737373] uppercase tracking-wide">Diminuzioni</p>
                <p className="text-xl font-semibold text-red-600 mt-0.5">{isZeroMoney(diminuzioniTotal) ? '' : '−'}{formatCurrency(diminuzioniTotal)}</p>
              </div>
              <div className="p-3.5 bg-[#FFF7F0]" data-testid="cv-header-aggiornato">
                <p className="text-[11px] text-[#FF7819] uppercase tracking-wide flex items-center gap-1"><Scale className="w-3 h-3" /> Valore aggiornato</p>
                <p className="text-xl font-bold text-[#FF7819] mt-0.5">{formatCurrency(computed)}</p>
                <p className={`text-xs mt-0.5 ${totalEffect >= 0 ? 'text-emerald-700' : 'text-red-600'}`} data-testid="cv-header-netta">
                  Variazione netta {moneySign(totalEffect)}{formatCurrency(Math.abs(totalEffect))}
                </p>
              </div>
            </div>
            {/* Micro-riga secondaria */}
            <div className="px-3.5 py-2 bg-slate-50 border-t border-slate-100 grid grid-cols-1 sm:grid-cols-3 gap-1.5 text-xs" data-testid="cv-breakdown">
              <div className="flex items-center justify-between sm:justify-start sm:gap-1.5">
                <span className="text-[#9a9a9a]">Extra fuori variante</span>
                <span className="font-medium text-emerald-700/80">{isZeroMoney(stdPos) ? '' : '+'}{formatCurrency(stdPos)}</span>
              </div>
              <div className="flex items-center justify-between sm:justify-start sm:gap-1.5">
                <span className="text-[#9a9a9a]">Diminuzioni fuori variante</span>
                <span className="font-medium text-red-600/80">{isZeroMoney(stdNeg) ? '' : '−'}{formatCurrency(stdNeg)}</span>
              </div>
              <div className="flex items-center justify-between sm:justify-start sm:gap-1.5">
                <span className="text-[#9a9a9a]">Varianti contrattuali</span>
                <span className={`font-medium ${variationNet >= 0 ? 'text-emerald-700/80' : 'text-red-600/80'}`}>{moneySign(variationNet)}{formatCurrency(Math.abs(variationNet))}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ============ A · SCELTE CLIENTE / FINITURE ============ */}
      <div className="order-2">
        <CustomerFinishesPanel
          projectId={projectId}
          extras={extrasLocal}
          canEdit={canOperateFinishes}
          canViewEconomic={canViewEconomic}
          projectClosed={projectClosed}
          onNewSelection={onNewSelection}
          onOpenExtra={onOpenExtra}
          onChanged={reloadAll}
        />
      </div>

      {/* ============ Barra azioni (Nuovo) — nessun heading ridondante ============ */}
      {(canReadVariations || canEditStandalone) && (
        <div className="flex items-center justify-end flex-wrap gap-2 order-3">
          {daGestireCount > 0 && (
            <span className="text-[11px] px-2 py-0.5 rounded bg-amber-100 text-amber-700 flex items-center gap-1" data-testid="cv-da-gestire-indicator">
              <AlertTriangle className="w-3 h-3" /> {daGestireCount} da gestire
            </span>
          )}
          {(canEditStandalone || canManageVariations) && !projectClosed && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button className="bg-[#FF7819] hover:bg-[#E56A0F]" data-testid="cv-new-menu-trigger">
                  <Plus className="w-4 h-4 mr-2" /> Nuovo
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-64">
                {canEditStandalone && (
                  <>
                    <DropdownMenuItem onClick={() => onNewExtra && onNewExtra('lavoro')} data-testid="cv-new-extra-lavoro">
                      <Wrench className="w-4 h-4 mr-2 text-emerald-600" /> Extra lavoro
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => onNewExtra && onNewExtra('fornitura')} data-testid="cv-new-extra-fornitura">
                      <Package className="w-4 h-4 mr-2 text-emerald-600" /> Extra fornitura
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setNegOpen(true)} data-testid="cv-new-diminuzione">
                      <Minus className="w-4 h-4 mr-2 text-red-500" /> Lavorazione in diminuzione
                    </DropdownMenuItem>
                  </>
                )}
                {canManageVariations && (
                  <DropdownMenuItem onClick={() => { setNewVarDesc(''); setNewVarOpen(true); }} data-testid="cv-new-variante">
                    <Layers className="w-4 h-4 mr-2 text-[#FF7819]" /> Variante contrattuale
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      )}

      {/* ============ C · SEZIONE VARIANTI CONTRATTUALI ============ */}
      {canReadVariations && (
        <div className="space-y-3 order-5" data-testid="cv-variations-section">
          <h3 className="text-h4">Varianti contrattuali</h3>
          {variations.length === 0 ? (
            <div className="rounded-lg border border-dashed border-[#E4E4E4] bg-white px-4 py-3 text-sm text-[#8a8a8a] flex items-center gap-2" data-testid="cv-variations-empty">
              <Layers className="w-4 h-4 text-[#D4D4D4]" /> Nessuna variante contrattuale
            </div>
          ) : (
            <div className="grid gap-3 grid-cols-[repeat(auto-fill,minmax(520px,1fr))]">
              {variations.map((v) => {
                const t = v.totals || {};
                const net = num(t.net_total);
                const st = VAR_STATUS[v.status] || { label: v.status, cls: 'bg-slate-100 text-slate-600' };
                return (
                  <button key={v.id} onClick={() => openVariation(v)} data-testid={`cv-variation-card-${v.id}`}
                    className="w-full text-left rounded-xl border border-[#ECECEC] bg-white p-3.5 hover:border-[#FF7819] hover:shadow-sm transition-colors">
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold text-[#171717]">{v.code}</span>
                          <span className={`text-[11px] px-2 py-0.5 rounded ${st.cls}`} data-testid={`cv-variation-status-${v.id}`}>{st.label}</span>
                          <span className="text-xs text-[#a3a3a3]">{fmtDate(v.effective_at || v.created_at)}</span>
                        </div>
                        <p className="text-sm text-[#525252] mt-0.5 truncate">{v.description || 'Variante contrattuale'}</p>
                        <div className="flex items-center gap-4 mt-1.5 text-xs">
                          <span className="text-emerald-700">{isZeroMoney(num(t.positive_total)) ? '' : '+ '}{formatCurrency(num(t.positive_total))} lavori</span>
                          <span className="text-red-600">{isZeroMoney(num(t.negative_total)) ? '' : '− '}{formatCurrency(num(t.negative_total))} diminuzioni</span>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-[11px] text-[#8a8a8a]">Netto</p>
                        <p className={`text-lg font-bold ${net >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>{moneySign(net)}{formatCurrency(Math.abs(net))}</p>
                        <ChevronRight className="w-4 h-4 text-[#c4c4c4] ml-auto" />
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ============ B · EXTRA E DIMINUZIONI FUORI VARIANTE ============ */}
      <div className="pt-2 order-4" data-testid="cv-standalone-section">
        <h3 className="text-h4 mb-3">Extra e diminuzioni fuori variante</h3>
        <ExtraVariazioniPanel
          projectId={projectId}
          extras={extrasLocal}
          salItems={salItems}
          canEdit={canEditStandalone}
          canViewEconomic={false}
          projectClosed={projectClosed}
          onNewExtra={onNewExtra}
          onOpenExtra={onOpenExtra}
          onChanged={reloadAll}
          hideSummary
          hideCta
          titleText=""
          hiddenExtraIds={hiddenStandaloneExtraIds}
          hiddenNegativeIds={absorbedNegativeIds}
        />
      </div>

      {/* ============ DIALOG: NUOVA VARIANTE ============ */}
      <Dialog open={newVarOpen} onOpenChange={(o) => !o && setNewVarOpen(false)}>
        <DialogContent data-testid="cv-new-variante-dialog">
          <DialogHeader>
            <DialogTitle>Nuova variante contrattuale</DialogTitle>
            <DialogDescription>Crea una bozza. Potrai associare lavorazioni aggiuntive e in diminuzione, poi renderla effettiva.</DialogDescription>
          </DialogHeader>
          <div>
            <label className="text-xs font-medium">Descrizione</label>
            <Input value={newVarDesc} onChange={(e) => setNewVarDesc(e.target.value)} placeholder="Es. Modifica distribuzione zona giorno" data-testid="cv-new-variante-desc" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewVarOpen(false)}>Annulla</Button>
            <Button onClick={createVariation} disabled={busy} className="bg-[#FF7819] hover:bg-[#E56A0F]" data-testid="cv-new-variante-confirm">Crea bozza</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ============ COMPOSER (bozza) ============ */}
      <Dialog open={!!composerVar && composerVar.status === 'bozza'} onOpenChange={(o) => !o && setComposerVar(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="cv-composer-dialog">
          {composerVar && (() => {
            const t = composerVar.totals || {};
            const eff = economicEffectLine(composerVar);
            return (
              <>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">{composerVar.code}
                    <span className="text-[11px] px-2 py-0.5 rounded bg-amber-100 text-amber-700">Bozza</span>
                  </DialogTitle>
                  <DialogDescription>{composerVar.description || 'Composizione variante contrattuale'}</DialogDescription>
                </DialogHeader>

                {/* Lavorazioni aggiuntive */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-semibold text-emerald-700 flex items-center gap-1"><TrendingUp className="w-4 h-4" /> Lavorazioni aggiuntive</h4>
                    <Button size="sm" variant="outline" onClick={() => setPicker('extra')} data-testid="cv-attach-extra-btn"><Plus className="w-3.5 h-3.5 mr-1" /> Associa Extra lavoro</Button>
                  </div>
                  {(composerVar.positive_components || []).length === 0 ? (
                    <p className="text-xs text-[#a3a3a3] italic px-1">Nessuna lavorazione aggiuntiva associata.</p>
                  ) : (
                    <div className="space-y-1">
                      {composerVar.positive_components.map((c) => (
                        <div key={c.extra_id} data-testid={`cv-pos-comp-${c.extra_id}`} className="flex items-center justify-between bg-emerald-50/60 rounded-lg px-3 py-2 text-sm">
                          <span className="text-[#171717]">{c.extra_number || 'Extra'}</span>
                          <div className="flex items-center gap-3">
                            <span className="font-semibold text-emerald-700">{isZeroMoney(num(c.value)) ? '' : '+'}{formatCurrency(num(c.value))}</span>
                            <button onClick={() => detach('extra', c.extra_id)} className="text-[#a3a3a3] hover:text-red-500" data-testid={`cv-detach-extra-${c.extra_id}`}><X className="w-4 h-4" /></button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Lavorazioni in diminuzione */}
                <div className="space-y-2 mt-2">
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-semibold text-red-600 flex items-center gap-1"><TrendingDown className="w-4 h-4" /> Lavorazioni in diminuzione</h4>
                    <Button size="sm" variant="outline" onClick={() => setPicker('negative')} data-testid="cv-attach-negative-btn"><Plus className="w-3.5 h-3.5 mr-1" /> Associa diminuzione</Button>
                  </div>
                  {(composerVar.negative_components || []).length === 0 ? (
                    <p className="text-xs text-[#a3a3a3] italic px-1">Nessuna diminuzione associata. Una variante richiede almeno una diminuzione.</p>
                  ) : (
                    <div className="space-y-1">
                      {composerVar.negative_components.map((c) => (
                        <div key={c.negative_variation_id} data-testid={`cv-neg-comp-${c.negative_variation_id}`} className="flex items-center justify-between bg-red-50/60 rounded-lg px-3 py-2 text-sm">
                          <span className="text-[#171717]">{c.item_code || 'Diminuzione'}</span>
                          <div className="flex items-center gap-3">
                            <span className="font-semibold text-red-600">{isZeroMoney(num(c.value)) ? '' : '−'}{formatCurrency(num(c.value))}</span>
                            <button onClick={() => detach('negative', c.negative_variation_id)} className="text-[#a3a3a3] hover:text-red-500" data-testid={`cv-detach-negative-${c.negative_variation_id}`}><X className="w-4 h-4" /></button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* LIVE PREVIEW */}
                <div className="mt-3 rounded-xl border border-[#ECECEC] bg-[#FAFAFA] p-4 space-y-1.5 text-sm" data-testid="cv-live-preview">
                  <p className="text-xs font-semibold text-[#737373] uppercase tracking-wide mb-1">Impatto previsto</p>
                  <div className="flex justify-between"><span className="text-[#525252]">Incrementi</span><span className="font-medium text-emerald-700">{isZeroMoney(num(t.positive_total)) ? '' : '+'}{formatCurrency(num(t.positive_total))}</span></div>
                  <div className="flex justify-between"><span className="text-[#525252]">Diminuzioni</span><span className="font-medium text-red-600">{isZeroMoney(num(t.negative_total)) ? '' : '−'}{formatCurrency(num(t.negative_total))}</span></div>
                  <div className="flex justify-between border-t border-slate-200 pt-1.5"><span className="text-[#171717] font-medium">Netto</span><span className={`font-bold ${num(t.net_total) >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>{moneySign(num(t.net_total))}{formatCurrency(Math.abs(num(t.net_total)))}</span></div>
                  <div className="flex justify-between text-xs pt-1"><span className="text-[#8a8a8a]">di cui sostituzione</span><span className="text-[#525252]">{formatCurrency(num(t.replacement_amount))}</span></div>
                  <div className={`flex justify-between text-xs font-medium ${eff.tone === 'red' ? 'text-red-600' : eff.tone === 'emerald' ? 'text-emerald-700' : 'text-[#525252]'}`}><span>Effetto</span><span data-testid="cv-preview-effect">{eff.text}</span></div>
                </div>

                {!canFinalize && (
                  <p className="text-xs text-amber-600 flex items-center gap-1 mt-1"><AlertTriangle className="w-3.5 h-3.5" /> Serve almeno una lavorazione in diminuzione per rendere effettiva la variante.</p>
                )}

                <DialogFooter className="mt-2">
                  <Button variant="ghost" onClick={deleteBozzaVariation} disabled={busy} className="text-red-500 hover:bg-red-50 mr-auto" data-testid="cv-composer-delete"><Trash2 className="w-4 h-4 mr-1" /> Elimina bozza</Button>
                  <Button variant="outline" onClick={() => setComposerVar(null)}>Chiudi</Button>
                  <Button onClick={() => setConfirmFinalize(true)} disabled={busy || !canFinalize} className="bg-[#FF7819] hover:bg-[#E56A0F]" data-testid="cv-finalize-btn"><CheckCircle2 className="w-4 h-4 mr-1" /> Rendi effettiva la variante</Button>
                </DialogFooter>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* ============ PICKER componenti eleggibili ============ */}
      <Dialog open={!!picker} onOpenChange={(o) => !o && setPicker(null)}>
        <DialogContent data-testid="cv-picker-dialog">
          <DialogHeader>
            <DialogTitle>{picker === 'extra' ? 'Associa Extra lavoro' : 'Associa diminuzione'}</DialogTitle>
            <DialogDescription>{picker === 'extra' ? 'Solo Extra lavoro non già assegnati ad un\'altra variante. Le forniture non sono ammesse.' : 'Solo diminuzioni in bozza non ancora associate.'}</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 max-h-[50vh] overflow-y-auto">
            {picker === 'extra' && (eligibleExtras.length === 0 ? (
              <p className="text-sm text-[#a3a3a3] italic" data-testid="cv-picker-empty">Nessun Extra lavoro eleggibile.</p>
            ) : eligibleExtras.map((e) => (
              <button key={e.id} onClick={() => attach('extra', e.id)} data-testid={`cv-pick-extra-${e.id}`}
                className="w-full flex items-center justify-between rounded-lg border border-[#ECECEC] px-3 py-2 hover:border-emerald-400 hover:bg-emerald-50 text-left">
                <div><p className="text-sm font-medium">{e.extra_number || 'Extra'}</p><p className="text-xs text-[#8a8a8a] truncate max-w-[300px]">{e.title || ''}</p></div>
                <span className="font-semibold text-emerald-700">{isZeroMoney(num(e.total)) ? '' : '+'}{formatCurrency(num(e.total))}</span>
              </button>
            )))}
            {picker === 'negative' && (
              <>
                {eligibleNegatives.length === 0 ? (
                  <p className="text-sm text-[#a3a3a3] italic" data-testid="cv-picker-empty">Nessuna diminuzione eleggibile. Creane una nuova.</p>
                ) : eligibleNegatives.map((n) => (
                  <button key={n.id} onClick={() => attach('negative', n.id)} data-testid={`cv-pick-negative-${n.id}`}
                    className="w-full flex items-center justify-between rounded-lg border border-[#ECECEC] px-3 py-2 hover:border-red-400 hover:bg-red-50 text-left">
                    <div><p className="text-sm font-medium">{n.item_code || 'Diminuzione'}</p><p className="text-xs text-[#8a8a8a] truncate max-w-[300px]">{n.item_description || ''}</p></div>
                    <span className="font-semibold text-red-600">{isZeroMoney(num(n.value)) ? '' : '−'}{formatCurrency(num(n.value))}</span>
                  </button>
                ))}
                <Button variant="outline" className="w-full" onClick={() => { setPicker(null); setNegOpen(true); }} data-testid="cv-picker-create-neg"><Plus className="w-4 h-4 mr-1" /> Crea nuova diminuzione</Button>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPicker(null)}>Chiudi</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ============ CONFERMA FINALIZE ============ */}
      <Dialog open={confirmFinalize} onOpenChange={(o) => !o && setConfirmFinalize(false)}>
        <DialogContent data-testid="cv-confirm-finalize-dialog">
          {composerVar && (() => {
            const t = composerVar.totals || {}; const eff = economicEffectLine(composerVar); const net = num(t.net_total);
            return (
              <>
                <DialogHeader>
                  <DialogTitle>Rendi effettiva la variante</DialogTitle>
                  <DialogDescription>La variante diventerà contrattualmente effettiva. Gli effetti economici e tecnici verranno applicati.</DialogDescription>
                </DialogHeader>
                <div className="space-y-1.5 text-sm bg-[#FAFAFA] rounded-lg p-3">
                  <div className="flex justify-between"><span className="text-[#737373]">Incrementi</span><span className="font-medium text-emerald-700">{isZeroMoney(num(t.positive_total)) ? '' : '+'}{formatCurrency(num(t.positive_total))}</span></div>
                  <div className="flex justify-between"><span className="text-[#737373]">Diminuzioni</span><span className="font-medium text-red-600">{isZeroMoney(num(t.negative_total)) ? '' : '−'}{formatCurrency(num(t.negative_total))}</span></div>
                  <div className="flex justify-between border-t border-slate-200 pt-1.5"><span className="text-[#171717] font-medium">Netto</span><span className={`font-bold ${net >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>{moneySign(net)}{formatCurrency(Math.abs(net))}</span></div>
                  <div className="flex justify-between text-xs"><span className="text-[#8a8a8a]">Sostituzione</span><span>{formatCurrency(num(t.replacement_amount))}</span></div>
                  <div className={`flex justify-between text-xs font-medium ${eff.tone === 'red' ? 'text-red-600' : eff.tone === 'emerald' ? 'text-emerald-700' : 'text-[#525252]'}`}><span>Effetto</span><span>{eff.text}</span></div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setConfirmFinalize(false)}>Annulla</Button>
                  <Button onClick={finalizeVariation} disabled={busy} className="bg-[#FF7819] hover:bg-[#E56A0F]" data-testid="cv-confirm-finalize-btn"><CheckCircle2 className="w-4 h-4 mr-1" /> Conferma</Button>
                </DialogFooter>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* ============ DETTAGLIO variante effective / da_gestire ============ */}
      <Dialog open={!!detailVar} onOpenChange={(o) => !o && setDetailVar(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="cv-detail-dialog">
          {detailVar && (() => {
            const t = detailVar.totals || {}; const eff = economicEffectLine(detailVar); const net = num(t.net_total);
            const st = VAR_STATUS[detailVar.status] || { label: detailVar.status, cls: 'bg-slate-100 text-slate-600' };
            return (
              <>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">{detailVar.code}<span className={`text-[11px] px-2 py-0.5 rounded ${st.cls}`}>{st.label}</span></DialogTitle>
                  <DialogDescription>{detailVar.description || 'Variante contrattuale'} · {fmtDate(detailVar.effective_at || detailVar.created_at)}</DialogDescription>
                </DialogHeader>

                {detailVar.status === 'da_gestire' && (
                  <div className="rounded-lg bg-red-50 border border-red-200 p-3 flex items-start gap-2" data-testid="cv-detail-da-gestire">
                    <AlertTriangle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
                    <div><p className="text-sm font-semibold text-red-700">Da gestire</p><p className="text-xs text-red-600">La riduzione non può essere assorbita automaticamente dal piano SAL residuo.</p></div>
                  </div>
                )}

                {(detailVar.positive_components || []).length > 0 && (
                  <div className="space-y-2">
                    <h4 className="text-sm font-semibold text-emerald-700">Lavorazioni aggiuntive</h4>
                    {detailVar.positive_components.map((c) => (
                      <div key={c.extra_id} className="flex justify-between text-sm px-1"><span className="text-[#525252]">{c.extra_number || 'Extra'}</span><span className="text-emerald-700">{isZeroMoney(num(c.value)) ? '' : '+'}{formatCurrency(num(c.value))}</span></div>
                    ))}
                  </div>
                )}

                {(detailVar.quantity_adjustments || []).length > 0 && (
                  <div className="space-y-2" data-testid="cv-detail-quantity-adjustments">
                    <h4 className="text-sm font-semibold text-sky-700 flex items-center gap-1"><Scale className="w-4 h-4" /> Rettifiche quantità</h4>
                    {detailVar.quantity_adjustments.map((a) => {
                      const sign = num(a.quantity_delta) >= 0 ? '+' : '−';
                      const valSign = num(a.value) >= 0 ? '+' : '−';
                      return (
                        <div key={a.id} data-testid={`cv-qa-${a.id}`} className="rounded-lg bg-sky-50/60 border border-sky-100 px-3 py-2 text-sm">
                          <div className="flex items-center justify-between">
                            <span className="font-medium text-[#171717]">{a.code || '—'} · {a.description || 'Voce'}</span>
                            <span className="font-semibold text-emerald-700">{isZeroMoney(num(a.value)) ? '' : valSign}{formatCurrency(Math.abs(num(a.value)))}</span>
                          </div>
                          <div className="flex flex-wrap gap-x-4 gap-y-0.5 mt-1 text-xs text-[#737373]">
                            <span>Delta: <span className="font-medium text-[#525252]">{sign}{Math.abs(num(a.quantity_delta))} {a.unit || ''}</span></span>
                            {(a.quantity_before != null && a.quantity_after != null) && (
                              <span>Quantità: {num(a.quantity_before)} → <span className="font-medium text-[#525252]">{num(a.quantity_after)}</span> {a.unit || ''}</span>
                            )}
                            {a.unit_price != null && <span>Prezzo unit.: {formatCurrency(num(a.unit_price))}</span>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {(detailVar.negative_components || []).length > 0 && (
                  <div className="space-y-2 mt-1">
                    <h4 className="text-sm font-semibold text-red-600">Lavorazioni in diminuzione</h4>
                    {detailVar.negative_components.map((c) => (
                      <div key={c.negative_variation_id} className="flex justify-between text-sm px-1"><span className="text-[#525252]">{c.item_code || 'Diminuzione'}</span><span className="text-red-600">{isZeroMoney(num(c.value)) ? '' : '−'}{formatCurrency(num(c.value))}</span></div>
                    ))}
                  </div>
                )}

                <div className="mt-2 rounded-xl border border-[#ECECEC] bg-[#FAFAFA] p-4 space-y-1.5 text-sm" data-testid="cv-detail-riepilogo">
                  <div className="flex justify-between"><span className="text-[#525252]">Incrementi</span><span className="font-medium text-emerald-700">{isZeroMoney(num(t.positive_total)) ? '' : '+'}{formatCurrency(num(t.positive_total))}</span></div>
                  <div className="flex justify-between"><span className="text-[#525252]">Diminuzioni</span><span className="font-medium text-red-600">{isZeroMoney(num(t.negative_total)) ? '' : '−'}{formatCurrency(num(t.negative_total))}</span></div>
                  <div className="flex justify-between"><span className="text-[#525252]">Sostituzione</span><span className="font-medium">{formatCurrency(num(t.replacement_amount))}</span></div>
                  {net >= 0
                    ? <div className="flex justify-between"><span className="text-[#525252]">Eccedenza</span><span className="font-medium text-emerald-700">{formatCurrency(num(t.excess_amount))}</span></div>
                    : <div className="flex justify-between"><span className="text-[#525252]">Riduzione contratto</span><span className="font-medium text-red-600">{formatCurrency(num(t.reduction_excess_amount))}</span></div>}
                  <div className="flex justify-between border-t border-slate-200 pt-1.5"><span className="text-[#171717] font-medium">Netto</span><span className={`font-bold ${net >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>{moneySign(net)}{formatCurrency(Math.abs(net))}</span></div>
                  <div className={`flex justify-between pt-1 font-medium ${eff.tone === 'red' ? 'text-red-600' : eff.tone === 'emerald' ? 'text-emerald-700' : 'text-[#525252]'}`}><span>Effetto economico</span><span data-testid="cv-detail-effect">{eff.text}</span></div>
                </div>

                {detailVar.status === 'effective' && (
                  <SubcontractImpactSection projectId={projectId} variationId={detailVar.id} canWrite={canManageVariations} />
                )}

                <DialogFooter>
                  {onOpenExtra && (detailVar.positive_components || []).some((c) => c.extra_id) && (
                    <Button
                      onClick={() => { const c = (detailVar.positive_components || []).find((x) => x.extra_id); const eid = c && c.extra_id; setDetailVar(null); if (eid) onOpenExtra(eid); }}
                      className="bg-[#FF7819] hover:bg-[#E56A0F] mr-auto"
                      data-testid="cv-detail-open-extra"
                    >
                      <Package className="w-4 h-4 mr-1" /> Apri Extra completo
                    </Button>
                  )}
                  <Button variant="outline" onClick={() => setDetailVar(null)}>Chiudi</Button>
                </DialogFooter>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* ============ DIALOG standalone diminuzione ============ */}
      <Dialog open={negOpen} onOpenChange={(o) => !o && setNegOpen(false)}>
        <DialogContent data-testid="cv-neg-dialog">
          <DialogHeader>
            <DialogTitle>Lavorazione in diminuzione</DialogTitle>
            <DialogDescription>Crea una bozza. Potrai approvarla come standalone oppure associarla a una variante contrattuale.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium">Voce contrattuale da ridurre *</label>
              <Select value={selItemId} onValueChange={setSelItemId}>
                <SelectTrigger data-testid="cv-neg-voce"><SelectValue placeholder="Seleziona una voce" /></SelectTrigger>
                <SelectContent>
                  {reducibleItems.map((i) => <SelectItem key={i.id} value={i.id}>{i.code} · {(i.description || '').slice(0, 50)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {selItem && (
              <div className="text-sm bg-[#FAFAFA] rounded-lg p-3 flex justify-between" data-testid="cv-neg-residuo">
                <span className="text-[#737373]">Valore diminuzione stimato</span>
                <span className="font-semibold text-red-600">{isZeroMoney(selResidual) ? '' : '−'}{formatCurrency(selResidual)}</span>
              </div>
            )}
            <div>
              <label className="text-xs font-medium">Causale</label>
              <Select value={causale} onValueChange={setCausale}>
                <SelectTrigger data-testid="cv-neg-causale"><SelectValue /></SelectTrigger>
                <SelectContent>{CAUSALI.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium">Motivazione *</label>
              <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} data-testid="cv-neg-reason" placeholder="Descrivi la diminuzione (min 8 caratteri)" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNegOpen(false)}>Annulla</Button>
            <Button onClick={saveNegBozza} disabled={busy} className="bg-[#FF7819] hover:bg-[#E56A0F]" data-testid="cv-neg-confirm"><FileEdit className="w-4 h-4 mr-1" /> Salva bozza</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

function errMsg(e, fallback) {
  const d = e?.response?.data?.detail;
  if (typeof d === 'object' && d?.message) return d.message;
  if (typeof d === 'string') return d;
  return fallback;
}

export default ContractVariationsTab;
